import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Cookie helpers (double-submit CSRF pattern). velo_csrf is set by the server
// as a NON-httpOnly cookie so we can read it here and echo as a header on
// every state-changing request.
const readCookie = (name) => {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(new RegExp("(^|; )" + name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&") + "=([^;]*)"));
  return match ? decodeURIComponent(match[2]) : null;
};

// Auth cookies are httpOnly (velo_token) or scoped for our use (velo_kind,
// velo_csrf) — we never need to read velo_token here. On explicit logout
// the server clears all three via Set-Cookie; we also nuke any stale
// localStorage entries left over from the pre-cookie era.
const purgeStaleLocal = () => {
  try {
    localStorage.removeItem("velo_token");
    localStorage.removeItem("velo_kind");
    localStorage.removeItem("velo_csrf");
  } catch (_) { /* private mode */ }
};

export const api = axios.create({ baseURL: API, withCredentials: true });

api.interceptors.request.use((cfg) => {
  // Attach CSRF header for any state-changing method — server ignores it for
  // Bearer-authenticated requests, so it's safe to always send.
  const method = (cfg.method || "get").toLowerCase();
  if (!["get", "head", "options"].includes(method)) {
    const csrf = readCookie("velo_csrf");
    if (csrf) cfg.headers["X-CSRF-Token"] = csrf;
  }
  // Legacy: keep Bearer fallback for any lingering localStorage token so a
  // user with an old tab doesn't get bounced mid-session. New logins never
  // persist to localStorage.
  const legacy = (() => { try { return localStorage.getItem("velo_token"); } catch (_) { return null; } })();
  if (legacy && !cfg.headers.Authorization) {
    cfg.headers.Authorization = `Bearer ${legacy}`;
  }
  return cfg;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    // Any 401 from the API means the stored session is no longer valid —
    // clear cookies (via /auth/logout best-effort) and bounce to login.
    const url = err?.config?.url || "";
    const isAuthCall = url.includes("/auth/login") || url.includes("/auth/admin-login") || url.includes("/shop/login");
    if (err?.response?.status === 401 && !isAuthCall) {
      purgeStaleLocal();
      // Fire and forget — cookies clear even if this fails (server sets past-date Set-Cookie)
      axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
      if (window.location.pathname !== "/") window.location.replace("/");
    }
    return Promise.reject(err);
  },
);

// Session kind is a UI-only hint (member vs shop dashboard). It lives in a
// NON-httpOnly cookie so React can read it on cold-load without a round-trip.
export const setToken = (t, kind) => {
  // Cookies are the source of truth now — this function only nukes any old
  // localStorage tokens so the axios legacy fallback never re-attaches.
  purgeStaleLocal();
  if (!t && kind === undefined) {
    // Explicit logout requested by caller.
    axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
  }
  // `kind` is intentionally ignored — the server writes velo_kind on login.
};

export const getToken = () => {
  // Callers that still probe for a token treat truthy = logged-in. Cookie
  // is httpOnly so we can't inspect it directly — surface the presence of
  // the readable velo_kind cookie as a session-alive signal.
  return readCookie("velo_kind");
};
export const getSessionKind = () => readCookie("velo_kind") || "user";
